import React, { useState, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    CircularProgress,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    alpha,
} from '@mui/material';
import { Close as CloseIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import {
    confirmCollegeStatsMatches,
    UnmatchedCollegePlayer,
    BaseballRecruitProfile,
} from 'api/BaseballRecruits';
import { useSnackbar } from 'notistack';
import useTheme from '@mui/system/useTheme';

interface CollegeStatsMatchReviewProps {
    open: boolean;
    onClose: () => void;
    unmatchedPlayers: UnmatchedCollegePlayer[];
    profiles: BaseballRecruitProfile[];
    groupUuid: string;
    statsUrl: string;
    onConfirmed: () => void;
}

function getProfileDisplayName(p: BaseballRecruitProfile): string {
    if (p.player_bio) {
        const bio = p.player_bio as Record<string, any>;
        const name = `${bio.first_name || ''} ${bio.last_name || ''}`.trim();
        if (name) return name;
    }
    if (p.scraped_data) {
        const sd = typeof p.scraped_data === 'string' ? JSON.parse(p.scraped_data) : p.scraped_data;
        const player = sd?.player;
        if (player) {
            const name = `${player.first_name || ''} ${player.last_name || ''}`.trim();
            if (name) return name;
        }
    }
    if (p.individual?.name) return p.individual.name;
    return `Profile ${p.uuid.slice(0, 8)}`;
}

const CollegeStatsMatchReview: React.FC<CollegeStatsMatchReviewProps> = ({
    open,
    onClose,
    unmatchedPlayers,
    profiles,
    groupUuid,
    statsUrl,
    onConfirmed,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const [confirming, setConfirming] = useState(false);
    const [selections, setSelections] = useState<Record<number, string>>(() => {
        const initial: Record<number, string> = {};
        unmatchedPlayers.forEach((player, idx) => {
            if (player.suggestions?.length && player.suggestions[0].score >= 0.6) {
                initial[idx] = player.suggestions[0].profile_uuid;
            }
        });
        return initial;
    });

    const profileOptions = useMemo(() => {
        return profiles.map((p) => ({
            uuid: p.uuid,
            name: getProfileDisplayName(p),
            hasCollegeStats: !!p.college_scraped_data,
        }));
    }, [profiles]);

    const assignedCount = Object.keys(selections).length;

    const handleConfirm = async () => {
        const matches = Object.entries(selections)
            .filter(([, uuid]) => uuid)
            .map(([idx, profileUuid]) => ({
                profile_uuid: profileUuid,
                college_data: unmatchedPlayers[Number(idx)].college_data,
            }));

        if (matches.length === 0) {
            enqueueSnackbar('No matches selected', { variant: 'warning' });
            return;
        }

        setConfirming(true);
        try {
            const response = await confirmCollegeStatsMatches(groupUuid, statsUrl, matches);
            enqueueSnackbar(response.data.message, { variant: 'success' });
            onConfirmed();
            onClose();
        } catch (err: any) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to confirm matches', {
                variant: 'error',
            });
        } finally {
            setConfirming(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">Match College Stats Players</Typography>
                    <IconButton onClick={onClose} disabled={confirming}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                        These college players could not be automatically matched to existing
                        profiles. Select which profile each player corresponds to, or leave blank to
                        skip.
                    </Typography>
                </Alert>

                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>College Player</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Info</TableCell>
                                <TableCell sx={{ fontWeight: 700, minWidth: 250 }}>
                                    Assign to Profile
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Suggestion</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {unmatchedPlayers.map((player, idx) => {
                                const topSuggestion = player.suggestions?.[0];
                                return (
                                    <TableRow
                                        key={idx}
                                        sx={
                                            selections[idx]
                                                ? {
                                                      bgcolor: alpha(
                                                          theme.palette.success.main,
                                                          0.05,
                                                      ),
                                                  }
                                                : {}
                                        }
                                    >
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={600}>
                                                {player.wmt_name}
                                            </Typography>
                                            {player.jersey && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    #{player.jersey}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {player.positions?.join(', ')}
                                                {player.class_year && ` · ${player.class_year}`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <FormControl fullWidth size="small">
                                                <Select
                                                    value={selections[idx] || ''}
                                                    onChange={(e) =>
                                                        setSelections((prev) => ({
                                                            ...prev,
                                                            [idx]: e.target.value as string,
                                                        }))
                                                    }
                                                    displayEmpty
                                                    sx={{ fontSize: '0.85rem' }}
                                                >
                                                    <MenuItem value="">
                                                        <em>Skip (no match)</em>
                                                    </MenuItem>
                                                    {profileOptions.map((opt) => (
                                                        <MenuItem key={opt.uuid} value={opt.uuid}>
                                                            {opt.name}
                                                            {opt.hasCollegeStats && (
                                                                <Chip
                                                                    label="has stats"
                                                                    size="small"
                                                                    sx={{
                                                                        ml: 1,
                                                                        height: 18,
                                                                        fontSize: '0.65rem',
                                                                    }}
                                                                />
                                                            )}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell>
                                            {topSuggestion ? (
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <Chip
                                                        label={`${(
                                                            topSuggestion.score * 100
                                                        ).toFixed(0)}%`}
                                                        size="small"
                                                        color={
                                                            topSuggestion.score >= 0.8
                                                                ? 'success'
                                                                : topSuggestion.score >= 0.6
                                                                ? 'warning'
                                                                : 'default'
                                                        }
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        {topSuggestion.name}
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    No close match
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={confirming} color="inherit">
                    Skip All
                </Button>
                <Button
                    variant="contained"
                    onClick={handleConfirm}
                    disabled={confirming || assignedCount === 0}
                    startIcon={confirming ? <CircularProgress size={18} /> : <CheckCircleIcon />}
                >
                    {confirming
                        ? 'Saving...'
                        : `Confirm ${assignedCount} Match${assignedCount !== 1 ? 'es' : ''}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CollegeStatsMatchReview;
